import EventEmitter from './event-emitter';
import DataSource from './data-source';
import {
    marketsIdServiceTransform,
    marketsImportFileTransform
} from './transforms/markets/id-service';
import { marketsPullQuoteTransform } from './transforms/markets/pull-quote';
import { marketsPullStatisticsTransform } from './transforms/markets/pull-statics';
import { marketStaticTransforms } from './transforms/markets/static';
import { marketsOptionGreeksTransform } from './transforms/markets/option-greeks';
import {
    marketsTreasuryTransform,
    marketsUsGovtTransform
} from './transforms/markets/bonds';
import { marketsExchangeInfoTransform } from './transforms/markets/exchange-info';

import {
    marketsNewsListTransform,
    marketsNewsViewTransform,
    marketsNewsLogosTransform,
    marketsNewsSourcesTransform,
    marketsNewsVendorsTransform
} from './transforms/markets/news';

import { marketsAutoCompleteTransform } from './transforms/markets/autocomplete';

import { marketsTimeSalesTransform } from './transforms/markets/time-sales';

import { marketsPermissionTransform } from './transforms/markets/permissions';

import {
    marketsAlertsTransform,
    marketsAlertConfigTransform
} from './transforms/markets/alert';
import {
    marketsResearchBullsVsBearsTransform,
    marketsResearchHighlightsTransform
} from './transforms/markets/research';

import url from '../util/url';

import {
    DATA_EVENTS,
    REQUEST_TIMEOUT,
    GROUP_SYMBOL_QUANTITY
} from './constant';

import log from '../util/logger';

import { extend, flattenDeep, hasProperty } from '../util/jquery';

const logModule = 'morningstar.markets.marketdata.apiManager';

export default class ApiManager extends EventEmitter {
    constructor() {
        super();
        this.tokenExpiredTriggeredCount = 0;
        this.pending = false;
        this.queue = [];
        this.option = {
            groupSymbolQuantity: GROUP_SYMBOL_QUANTITY // will split the request by 100 symbols
        };
        this.dataSources = {
            '@web/ra/idService': new DataSource({
                transformationPipeline: [marketsIdServiceTransform]
            }),
            '@pull/ra/quoteData': new DataSource({
                transformationPipeline: [marketsPullQuoteTransform]
            }),
            '@pull/ra/getGLAData': new DataSource({
                transformationPipeline: [marketsPullStatisticsTransform]
            }),
            '@web/basicInfo.jsp': new DataSource({
                transformationPipeline: [marketsExchangeInfoTransform]
            }),
            '@web/news/v1/list': new DataSource({
                transformationPipeline: [marketsNewsListTransform]
            }),
            '@web/news/v1/view': new DataSource({
                transformationPipeline: [marketsNewsViewTransform]
            }),
            '@web/news/v1/mostViewed': new DataSource({
                transformationPipeline: [marketsNewsListTransform]
            }),
            '@web/news/v1/logos': new DataSource({
                transformationPipeline: [marketsNewsLogosTransform]
            }),
            '@web/news/v1/entitledSources': new DataSource({
                transformationPipeline: [marketsNewsSourcesTransform]
            }),
            '@web/news/v1/vendors': new DataSource({
                transformationPipeline: [marketsNewsVendorsTransform]
            }),
            '@web/ra/uniqueChartData': new DataSource(),
            '@web/service/staticData': new DataSource({
                transformationPipeline: [marketStaticTransforms]
            }),
            '@web/ra/obtainOptionchain': new DataSource(),
            '@pull/ra/greeks': new DataSource({
                transformationPipeline: [marketsOptionGreeksTransform]
            }),
            '@web/ra/getTreasuryYieldsSnapshot': new DataSource({
                transformationPipeline: [marketsTreasuryTransform]
            }),
            '@web/ra/getTreasuryYieldHistory': new DataSource({
                transformationPipeline: [marketsTreasuryTransform]
            }),
            '@web/invoke/getUSGovtBond': new DataSource({
                transformationPipeline: [marketsUsGovtTransform]
            }),
            '@web/trackComponentUsage.jsp': new DataSource(),
            '@web/service/queryPermission': new DataSource({
                transformationPipeline: [marketsPermissionTransform]
            }),
            '@web/ra/autocomplete': new DataSource({
                transformationPipeline: [marketsAutoCompleteTransform]
            }),
            '@web/ra/timeSales': new DataSource({
                transformationPipeline: [marketsTimeSalesTransform]
            }),
            '@web/alert/v2/getActiveAlerts': new DataSource({
                transformationPipeline: [marketsAlertsTransform]
            }),
            '@web/alert/v2/getTriggeredAlerts': new DataSource({
                transformationPipeline: [marketsAlertsTransform]
            }),
            '@web/alert/v2/updateAlert': new DataSource({
                transformationPipeline: [marketsAlertsTransform]
            }),
            '@web/alert/v2/subscribeAlert': new DataSource({
                transformationPipeline: [marketsAlertsTransform]
            }),
            '@web/alert/v2/deleteAlert': new DataSource({
                transformationPipeline: [marketsAlertsTransform]
            }),
            '@web/alert/v2/acknowledgeAlerts': new DataSource({
                transformationPipeline: [marketsAlertsTransform]
            }),
            '@web/alert/v2/saveAlertConfig': new DataSource({
                transformationPipeline: [marketsAlertConfigTransform]
            }),
            '@web/alert/v2/getAlertConfig': new DataSource({
                transformationPipeline: [marketsAlertConfigTransform]
            }),
            // research api.
            '@web/ra/getResearchHighlights': new DataSource({
                transformationPipeline: [marketsResearchHighlightsTransform]
            }),
            '@web/ra/getResearchBullsVsBears': new DataSource({
                transformationPipeline: [marketsResearchBullsVsBearsTransform]
            }),
            '@web/ra/getResearchCompetitors': new DataSource(),
            '@web/ra/importWatchlist': new DataSource({
                transformationPipeline: [marketsImportFileTransform]
            }),
            '@web/ra/videos': new DataSource(),
            '@web/ra/doc/regulatoryFilings': new DataSource(),
            '@web/ra/exportWatchlist': new DataSource(),
            '@web/ra/exportExcelRTD': new DataSource()
        };
    }
    setOption(option) {
        if (
            (this.option.sessionId &&
                this.option.sessionId !== option.sessionId) ||
            (this.option.accessToken &&
                this.option.accessToken !== option.accessToken)
        ) {
            this.emit(DATA_EVENTS.TOKEN_RENEWED, option);
        }
        extend(this.option, option);
        const dsOption = {
            accessToken: this.option.accessToken,
            sessionId: this.option.sessionId,
            instid: this.option.instid,
            timeout: this.option.timeout,
            axiosInstance: this.option.axiosInstance
        };
        for (const serviceKey in this.dataSources) {
            this.dataSources[serviceKey].options(
                extend(
                    {
                        url: url({
                            serviceKey,
                            env: this.option.env,
                            isAPIGateway: !!this.option.accessToken,
                            overridePaths: this.option.overridePaths
                        })
                    },
                    dsOption
                )
            );
        }
        this.queue.forEach(q => {
            q.dataSource.options(dsOption);
        });
    }
    async lookupSecurities(symbolArray) {
        const count = Math.ceil(
            symbolArray.length / this.option.groupSymbolQuantity
        );
        const executeQueue = [];
        for (let i = 0; i < count; i++) {
            executeQueue.push(
                this._wrap(this.dataSources['@web/ra/idService'], {
                    symbol: symbolArray
                        .slice(
                            this.option.groupSymbolQuantity * i,
                            this.option.groupSymbolQuantity * (i + 1)
                        )
                        .join(',')
                })
            );
        }
        const securitiesArray = await Promise.all(executeQueue);
        return flattenDeep(securitiesArray);
    }
    getExchangeDetails() {
        return this._wrap(this.dataSources['@web/basicInfo.jsp']);
    }
    async quoteMarketsPull({
        instruments,
        method = 'get',
        timeout = REQUEST_TIMEOUT,
        updateLastAccessTime = true
    }) {
        const count = Math.ceil(
            instruments.length / this.option.groupSymbolQuantity
        );
        const executeQueue = [];
        const dataSource = this.dataSources['@pull/ra/quoteData'].options({
            method,
            timeout
        });
        for (let i = 0; i < count; i++) {
            executeQueue.push(
                this._wrap(dataSource, {
                    query: instruments
                        .slice(
                            this.option.groupSymbolQuantity * i,
                            this.option.groupSymbolQuantity * (i + 1)
                        )
                        .join(','),
                    updateLastAccessTime: updateLastAccessTime,
                    qv: 1
                })
            );
        }
        const staticsArray = await Promise.all(executeQueue);
        return flattenDeep(staticsArray);
    }
    quoteMarketsStatistics(params) {
        params.qv = 1;
        return this._wrap(this.dataSources['@pull/ra/getGLAData'], params);
    }
    timeSeriesRealtimeMarkets(params) {
        return this._wrap(this.dataSources['@web/ra/uniqueChartData'], params);
    }
    getNewsHeadlines(params) {
        return this._wrap(this.dataSources['@web/news/v1/list'], params);
    }
    getNewsView(params) {
        return this._wrap(this.dataSources['@web/news/v1/view'], params);
    }
    getNewsMostViewed(params) {
        return this._wrap(this.dataSources['@web/news/v1/mostViewed'], params);
    }
    getNewsLogos(productCode, ids, size) {
        return this._wrap(this.dataSources['@web/news/v1/logos'], {
            productCode,
            ids: (ids || []).join(','),
            size
        });
    }
    getNewsSources(productCode, status) {
        return this._wrap(this.dataSources['@web/news/v1/entitledSources'], {
            productCode,
            status
        });
    }
    getNewsVendors(productCode) {
        return this._wrap(this.dataSources['@web/news/v1/vendors'], {
            productCode
        });
    }
    async getMarketStatic(
        instruments,
        fields = [],
        method = 'get',
        timeout = REQUEST_TIMEOUT
    ) {
        const count = Math.ceil(
            instruments.length / this.option.groupSymbolQuantity
        );
        const executeQueue = [];
        const dataSource = this.dataSources['@web/service/staticData'].options({
            method,
            timeout
        });
        for (let i = 0; i < count; i++) {
            executeQueue.push(
                this._wrap(dataSource, {
                    secId: instruments
                        .slice(
                            this.option.groupSymbolQuantity * i,
                            this.option.groupSymbolQuantity * (i + 1)
                        )
                        .join(','),
                    tfTokens: fields.join(',')
                })
            );
        }
        const quotesArray = await Promise.all(executeQueue);
        return flattenDeep(quotesArray);
    }
    getOptionChain(params) {
        return this._wrap(
            this.dataSources['@web/ra/obtainOptionchain'],
            params
        );
    }
    async getOptionGreeks(params) {
        const maxLength = this.option.groupSymbolQuantity - 1;
        const count = Math.ceil(params.instruments.length / maxLength);
        const executeQueue = [];
        const dataSource = this.dataSources['@pull/ra/greeks'];
        for (let i = 0; i < count; i++) {
            executeQueue.push(
                this._wrap(dataSource, {
                    options: params.instruments
                        .slice(maxLength * i, maxLength * (i + 1))
                        .join(','),
                    root: params.rootInstrument,
                    updateLastAccessTime: params.updateLastAccessTime,
                    qv: 1
                })
            );
        }
        const greeksArray = await Promise.all(executeQueue);
        return greeksArray.reduce(
            (accumulator, currentValue) => {
                accumulator.greeks = accumulator.greeks.concat(
                    currentValue.greeks
                );
                accumulator.rootInstrument = currentValue.rootInstrument;
                return accumulator;
            },
            {
                greeks: [],
                rootInstrument: ''
            }
        );
    }
    getTreasury(params) {
        if (params && params.timePeriods) {
            params.timePeriods = params.timePeriods.join(',');
        }
        if (params.sdate && params.edate) {
            return this._wrap(
                this.dataSources['@web/ra/getTreasuryYieldHistory'],
                {
                    sd: params.sdate,
                    ed: params.edate,
                    timePeriods: params.timePeriods
                }
            );
        }
        return this._wrap(
            this.dataSources['@web/ra/getTreasuryYieldsSnapshot'],
            params
        );
    }
    getGovernment() {
        return this._wrap(this.dataSources['@web/invoke/getUSGovtBond']);
    }
    sendTracking(params) {
        return this.dataSources['@web/trackComponentUsage.jsp']
            .execute(params)
            .catch(() => {});
    }
    queryPermission(params) {
        return this._wrap(
            this.dataSources['@web/service/queryPermission'],
            params
        );
    }
    autocomplete(params) {
        return this._wrap(this.dataSources['@web/ra/autocomplete'], params);
    }
    timeSales(params) {
        return this._wrap(this.dataSources['@web/ra/timeSales'], params);
    }
    getActiveAlerts(params) {
        params._t = new Date().getTime();
        if (!hasProperty(params, 'showAutoReset')) {
            params.showAutoReset = true;
        }
        if (!hasProperty(params, 'order')) {
            params.order = 'desc';
        }
        return this._wrap(
            this.dataSources['@web/alert/v2/getActiveAlerts'],
            params
        );
    }
    getTriggeredAlerts(params) {
        params._t = new Date().getTime();
        return this._wrap(
            this.dataSources['@web/alert/v2/getTriggeredAlerts'],
            params
        );
    }
    updateAlert(params) {
        return this._wrap(
            this.dataSources['@web/alert/v2/updateAlert'],
            params
        );
    }
    subscribeAlert(params) {
        return this._wrap(
            this.dataSources['@web/alert/v2/subscribeAlert'],
            params
        );
    }
    deleteAlert(params) {
        return this._wrap(
            this.dataSources['@web/alert/v2/deleteAlert'],
            params
        );
    }
    saveAlertConfig(params) {
        return this._wrap(
            this.dataSources['@web/alert/v2/saveAlertConfig'],
            params
        );
    }
    getAlertConfig(params = {}) {
        params._t = new Date().getTime();
        return this._wrap(
            this.dataSources['@web/alert/v2/getAlertConfig'],
            params
        );
    }
    acknowledgeAlerts(params) {
        return this._wrap(
            this.dataSources['@web/alert/v2/acknowledgeAlerts'],
            params
        );
    }
    /**
     * Get research highlights data.
     * @param params - some parameters collection.
     * @param params.symbol -  the request required symbol.
     * @param params.exchangeId -  the request required exchange id (mic code).
     * @returns {*|Promise<Object>}
     */
    getResearchHighlights(params) {
        return this._wrap(
            this.dataSources['@web/ra/getResearchHighlights'],
            params
        );
    }
    /**
     * Get research bulls bs bears data.
     * @param params - some parameters collection.
     * @param params.symbol -  the request required symbol.
     * @param params.exchangeId -  the request required exchange id (mic code).
     * @returns {*|Promise<Object>}
     */
    getResearchBullsVsBears(params) {
        return this._wrap(
            this.dataSources['@web/ra/getResearchBullsVsBears'],
            params
        );
    }
    /**
     * Get research competitors data.
     * @param params - some parameters collection.
     * @param params.secId -  the request required sec id.
     * @returns {*|Promise<Object>}
     */
    getResearchCompetitors(params) {
        return this._wrap(
            this.dataSources['@web/ra/getResearchCompetitors'],
            params
        );
    }
    securitiesByFormData(params, method = 'post') {
        return this._wrap(
            this.dataSources['@web/ra/importWatchlist'].options({
                method
            }),
            params
        );
    }
    getVideos(params) {
        return this._wrap(this.dataSources['@web/ra/videos'], params);
    }
    /**
     * Get regulatory filings document list
     * @param params - some parameters collection.
     * @param params.performanceIds -  A valid performanceId could be performance id.
     * @returns {*|Promise<Object>}
     */
    getRegulatoryFilings(params) {
        return this._wrap(
            this.dataSources['@web/ra/doc/regulatoryFilings'],
            params
        );
    }

    /**
     * Request blob object of watchlist excel to be exported
     * @param params
     * @param params.formData -  the form data to the posted .
     * @returns {*|Promise<Object>}
     */
    exportWatchlist(params, options) {
        return this._wrap(
            this.dataSources['@web/ra/exportWatchlist'].options(options),
            params
        );
    }

    /**
     * Request blob object of excel to be exported in excel RTD
     * @param params
     * @param params.formData -  the form data to the posted .
     * @returns {*|Promise<Object>}
     */
    exportExcelRTD(params, options) {
        return this._wrap(
            this.dataSources['@web/ra/exportExcelRTD'].options(options),
            params
        );
    }

    _execute({
        dataSource,
        params,
        resolve,
        reject,
        isCheckCall = false
    } = {}) {
        dataSource['execute'](params)
            .then(res => {
                // if the current one is resolved, then retry other failed one.
                this.pending = false;
                this.tokenExpiredTriggeredCount = 0;
                while (this.queue.length) {
                    this._execute(this.queue.pop());
                }
                resolve(res);
            })
            .catch(err => {
                let fulfilled = false;
                let isUpperLimit = false;
                if (err.code === -1) {
                    // other failed will put into cache queue and don't trigger the event
                    if (this.pending && !isCheckCall) {
                        this.queue.push({
                            dataSource,
                            params,
                            resolve,
                            reject
                        });
                        return;
                    }
                    // the first response one will be the check call. And only this call can trigger the event
                    if (this.events.has(DATA_EVENTS.TOKEN_EXPIRED)) {
                        fulfilled =
                            this.tokenExpiredTriggeredCount <
                            this.option.tokenExpiredTriggeredCount;
                        isUpperLimit = !fulfilled;
                    }
                }
                if (fulfilled) {
                    this.pending = true;
                    log.info(
                        logModule,
                        `Event ${DATA_EVENTS.TOKEN_EXPIRED} is triggered`,
                        extend({}, this.option)
                    );
                    this.emit(
                        DATA_EVENTS.TOKEN_EXPIRED,
                        extend({}, this.option),
                        option => {
                            this.tokenExpiredTriggeredCount++;
                            this.setOption(option);
                            // retry the check call
                            this._execute({
                                dataSource,
                                params,
                                resolve,
                                reject,
                                isCheckCall: true
                            });
                        }
                    );
                } else {
                    this.pending = false;
                    if (isUpperLimit) {
                        log.error(
                            logModule,
                            `Event ${DATA_EVENTS.TOKEN_EXPIRED} is triggered to reached upper limit ${this.tokenExpiredTriggeredCount}. But new token is still invalid.`
                        );
                        // retry count is reached upper limit then reject all
                        while (this.queue.length) {
                            this.queue.pop().reject(err);
                        }
                    }
                    reject(err);
                }
            });
    }
    _wrap(dataSource, params) {
        return new Promise((resolve, reject) => {
            if (this.pending) {
                this.queue.push({
                    dataSource,
                    params,
                    resolve,
                    reject
                });
            } else {
                this._execute({
                    dataSource,
                    params,
                    resolve,
                    reject
                });
            }
        });
    }
}
